import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'; // FAANG Level Universal Localization
import App from './App.jsx'
import * as Sentry from "@sentry/react";
import { registerSW } from 'virtual:pwa-register';
import { HelmetProvider } from 'react-helmet-async';

// Force PWA Installation on Load
registerSW({ immediate: true });

try {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      ignoreErrors: [
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'ChunkLoadError'
      ],
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        Sentry.feedbackIntegration({
          colorScheme: "system",
          isNameRequired: true,
          isEmailRequired: true,
        }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      tracePropagationTargets: ["localhost", /^https:\/\/dynamicexams\.org\/api/],
    });
  }
} catch(e) {
  console.error('Failed to initialize Sentry telemetry:', e);
}

import { ApolloClient, InMemoryCache, ApolloProvider, HttpLink } from '@apollo/client';

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL || '/graphql',
  useGETForQueries: true // 🚀 GOD MODE: Enables Cloudflare Edge Caching for GraphQL SWR
});

const client = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  }
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <HelmetProvider>
      <ApolloProvider client={client}>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100svh', background: '#0a0a0a', color: '#00e5ff' }}><h2>Loading National Framework...</h2></div>}>
          <Sentry.ErrorBoundary 
            fallback={({ error, resetError }) => {
              // Auto-recover from Vite ChunkLoadErrors (legacy or modern) caused by mid-session deployments
              if (error?.message?.includes('Failed to fetch dynamically imported module') || error?.message?.includes('SystemJS')) {
                if (!sessionStorage.getItem('chunk_load_retried')) {
                  sessionStorage.setItem('chunk_load_retried', 'true');
                  window.location.reload(true);
                  return <div className="h-screen w-screen bg-black flex items-center justify-center text-blue-500 font-mono">Applying Updates...</div>;
                }
              }

              return (
              <div className="flex flex-col items-center justify-center h-screen w-screen p-8 bg-zinc-950 text-white font-sans text-center">
                <div className="bg-zinc-900 border border-red-900 p-8 rounded-xl max-w-lg shadow-2xl">
                  <h1 className="text-3xl font-bold text-red-500 mb-4">Application Error</h1>
                  <p className="text-zinc-400 mb-6 text-sm">Our systems detected a critical fault. The engineering team has been notified via Sentry telemetry.</p>
                  <div className="bg-black p-4 rounded text-left overflow-auto max-h-32 mb-6 border border-zinc-800">
                    <code className="text-red-400 text-xs">{error?.message || 'Unknown error'}</code>
                  </div>
                  <button onClick={() => { sessionStorage.removeItem('chunk_load_retried'); resetError(); window.location.reload(); }} className="bg-white text-black px-6 py-2 rounded-lg font-bold hover:bg-zinc-200 transition-colors">
                    Try Recovering
                  </button>
                </div>
              </div>
            )}}
          >
            <App />
          </Sentry.ErrorBoundary>
        </Suspense>
      </ApolloProvider>
    </HelmetProvider>
  </StrictMode>,
)
